import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Camera, Plus, Trash2, Eye, EyeOff, Instagram, Loader2, Star, StarOff,
  CheckCircle, Clock, AlertCircle, Upload,
} from "lucide-react";

/**
 * Phase 25 — Admin gallery: owner uploads, moderation queue, IG auto-post.
 */
export default function AdminGalleryPage() {
  const [photos, setPhotos] = useState([]);
  const [ig, setIg] = useState({ connected: false });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, status] = await Promise.all([
        api.get("/admin/gallery"),
        api.get("/admin/gallery/instagram-status"),
      ]);
      setPhotos(p.data.items || []);
      setIg(status.data || { connected: false });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Photo must be under 4 MB"); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        await api.post("/admin/gallery", {
          image: String(reader.result || ""),
          caption: "", visible: true, in_carousel: true,
        });
        toast.success("Photo uploaded");
        await refresh();
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const toggle = async (p, k) => {
    await api.patch(`/admin/gallery/${p.id}`, { [k]: !p[k] });
    await refresh();
  };
  const approve = async (p) => {
    await api.post(`/admin/gallery/${p.id}/approve`);
    toast.success("Approved");
    await refresh();
  };
  const remove = async (p) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/admin/gallery/${p.id}`);
    await refresh();
  };
  const igPost = async (p) => {
    if (!ig.connected) {
      toast.error("Connect Instagram in Integrations Hub first.");
      return;
    }
    try {
      await api.post(`/admin/gallery/${p.id}/instagram-post`);
      toast.success("Posted to Instagram!");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "IG post failed");
    }
  };

  const filtered = photos.filter((p) => filter === "all" ? true : p.status === filter);
  const counts = {
    all: photos.length,
    pending: photos.filter((p) => p.status === "pending").length,
    approved: photos.filter((p) => p.status === "approved").length,
    owner: photos.filter((p) => p.status === "owner").length,
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-gallery-page">
      <header className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center">
          <Camera className="w-5 h-5 text-rose-700" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Gallery</h1>
          <p className="text-sm text-stone-500">
            Your storefront gallery, customer submissions, and one-tap Instagram posting.
          </p>
        </div>
      </header>

      {/* Instagram banner */}
      <div className={`mb-6 rounded-2xl p-4 border flex items-center gap-3 ${
        ig.connected
          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
          : "bg-stone-50 border-stone-200 text-stone-700"
      }`} data-testid="gallery-ig-status">
        <Instagram className="w-5 h-5" />
        <div className="flex-1 text-sm">
          {ig.connected ? (
            <>Instagram is connected to <code className="font-mono text-xs">{ig.ig_business_id}</code>. Click <b>Post to Instagram</b> on any photo.</>
          ) : (
            <>Instagram isn't connected yet. Add your <b>Meta Graph access token</b> + <b>Instagram Business ID</b> in <a href="/admin/integrations" className="underline">Integrations Hub</a> to enable one-click posting.</>
          )}
        </div>
      </div>

      {/* Upload + filters */}
      <div className="mb-5 flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {Object.entries(counts).map(([k, n]) => (
            <button
              key={k} onClick={() => setFilter(k)}
              data-testid={`gallery-filter-${k}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${
                filter === k ? "bg-stone-900 text-white border-stone-900"
                             : "bg-white border-stone-200 text-stone-600 hover:bg-stone-50"
              }`}
            >
              {k} <span className="opacity-60">· {n}</span>
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 bg-stone-900 text-white text-sm font-medium px-4 py-2 rounded-lg cursor-pointer hover:bg-stone-800">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Upload photo
          <input
            type="file" accept="image/*"
            className="hidden" onChange={upload}
            data-testid="gallery-admin-upload"
          />
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-stone-500 italic py-10 text-center">No photos in this category.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-admin-grid">
          {filtered.map((p) => (
            <div key={p.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`gallery-card-${p.id}`}>
              <div className="relative aspect-square bg-stone-100">
                <img
                  src={p.image_data_url || p.public_url}
                  alt={p.caption || ""}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute top-2 left-2 inline-flex gap-1">
                  <StatusPill status={p.status} />
                  {p.in_carousel && <span className="bg-amber-100 text-amber-800 text-[9px] px-2 py-0.5 rounded-full border border-amber-200">Carousel</span>}
                  {p.ig_posted && <span className="bg-pink-100 text-pink-800 text-[9px] px-2 py-0.5 rounded-full border border-pink-200">IG</span>}
                </div>
              </div>
              <div className="p-3 text-xs">
                {p.caption && <div className="text-stone-800 line-clamp-2 mb-1">{p.caption}</div>}
                {p.customer_name && <div className="text-stone-500">— {p.customer_name}</div>}
                <div className="flex gap-1 mt-2 flex-wrap">
                  {p.status === "pending" && (
                    <button onClick={() => approve(p)} data-testid={`gallery-approve-${p.id}`}
                            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100">
                      <CheckCircle className="w-3 h-3" /> Approve
                    </button>
                  )}
                  <button onClick={() => toggle(p, "visible")} data-testid={`gallery-toggle-visible-${p.id}`}
                          className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-stone-50 border border-stone-200 text-stone-700 hover:bg-stone-100">
                    {p.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} {p.visible ? "Visible" : "Hidden"}
                  </button>
                  <button onClick={() => toggle(p, "in_carousel")} data-testid={`gallery-toggle-carousel-${p.id}`}
                          className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-stone-50 border border-stone-200 text-stone-700 hover:bg-stone-100">
                    {p.in_carousel ? <Star className="w-3 h-3" /> : <StarOff className="w-3 h-3" />} Carousel
                  </button>
                  {ig.connected && !p.ig_posted && (
                    <button onClick={() => igPost(p)} data-testid={`gallery-ig-${p.id}`}
                            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-50 border border-pink-200 text-pink-700 hover:bg-pink-100">
                      <Instagram className="w-3 h-3" /> Post
                    </button>
                  )}
                  <button onClick={() => remove(p)} data-testid={`gallery-delete-${p.id}`}
                          className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { c: "bg-amber-100 text-amber-800 border-amber-200", l: "Pending" },
    approved: { c: "bg-emerald-100 text-emerald-800 border-emerald-200", l: "Approved" },
    owner: { c: "bg-stone-900 text-white border-stone-900", l: "Owner" },
  };
  const m = map[status] || map.pending;
  return <span className={`text-[9px] px-2 py-0.5 rounded-full border ${m.c}`}>{m.l}</span>;
}
